import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { SeguridadWorkspaceService } from './workspace/seguridad-workspace.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private permisos = new Set<string>();
  private esAdminSistema = false;
  private esAdminEmpresa = false;
  private cargado = false;
  private cargandoPromise: Promise<void> | null = null;
  private apiKeyDeLaCarga: string | null = null;

  constructor(
    private seguridadSvc: SeguridadWorkspaceService,
    private requestSvc: RequestService
  ) {}

  private currentApiKey(): string | null {
    return this.requestSvc.master?.apiKey || null;
  }

  /**
   * Carga la matriz de permisos del usuario si aun no esta hidratada.
   * Si el token cambio desde la ultima carga (otro usuario logueado),
   * invalida y recarga.
   */
  async loadIfNeeded(): Promise<void> {
    const currentKey = this.currentApiKey();

    // Token cambio (logout + login con otro usuario) → invalidar
    if (this.cargado && this.apiKeyDeLaCarga !== currentKey) {
      this.invalidate();
    }

    if (this.cargado) return;
    if (this.cargandoPromise) return this.cargandoPromise;

    this.cargandoPromise = this.cargar(currentKey);
    try {
      await this.cargandoPromise;
    } finally {
      this.cargandoPromise = null;
    }
  }

  /**
   * Fuerza recarga (util despues de cambios en roles/scopes del usuario).
   */
  async refresh(): Promise<void> {
    this.cargado = false;
    await this.loadIfNeeded();
  }

  /**
   * Limpia el cache (logout).
   */
  invalidate(): void {
    this.permisos = new Set();
    this.esAdminSistema = false;
    this.esAdminEmpresa = false;
    this.cargado = false;
    this.apiKeyDeLaCarga = null;
  }

  /**
   * Chequeo sincrono. Asume que loadIfNeeded() ya fue llamado.
   * Admins (sistema o empresa) bypasean siempre.
   */
  can(codigo: string): boolean {
    return (
      this.esAdminSistema ||
      this.esAdminEmpresa ||
      this.permisos.has(codigo)
    );
  }

  /**
   * Chequeo bulk: retorna un mapa { codigo: boolean }.
   */
  canMany(codigos: string[]): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const c of codigos) result[c] = this.can(c);
    return result;
  }

  esAdmin(): boolean {
    return this.esAdminSistema || this.esAdminEmpresa;
  }

  isLoaded(): boolean {
    return this.cargado;
  }

  private async cargar(apiKey: string | null): Promise<void> {
    try {
      const data = await this.seguridadSvc.getMisPermisos();
      this.permisos = new Set(data.permisos);
      this.esAdminSistema = !!data.esAdminSistema;
      this.esAdminEmpresa = !!data.esAdminEmpresa;
      this.cargado = true;
      this.apiKeyDeLaCarga = apiKey;
    } catch (e) {
      // Falla silenciosa: deja la matriz vacia, el usuario vera SinPermiso
      this.permisos = new Set();
      this.esAdminSistema = false;
      this.esAdminEmpresa = false;
      this.cargado = true;
      this.apiKeyDeLaCarga = apiKey;
    }
  }
}
